import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, LifeBuoy, Settings, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { BranchSwitcher } from '../BranchSwitcher';
import { ClinicSwitcher } from '../ClinicSwitcher';
import { NavCount, NavLock } from './NavItem';
import { shellCssVars } from './layout-contract';
import { useLabel } from './use-label';
import {
  useActiveNav,
  useVisibleNav,
  useVisibleSettings,
} from './use-nav-state';
import type { NavGroup, NavIcon, NavLeaf } from './nav-config';
import type { SidebarNavProps } from './SidebarNav';

/**
 * The menu behind "More" on a phone or tablet, laid out the way a phone
 * app's settings-style menu is: a title bar with a close button, the clinic
 * and branch on a card of their own, then each nav section as an inset
 * grouped list. Rows are a full 48px tap with a tinted icon tile and a
 * chevron; a group opens in place, its pages listed inside the same card.
 *
 * Same data as the desktop rail (nav-config, permissions, live counters,
 * the open-group state), only drawn for touch, so the two cannot drift.
 */
export type MobileMenuProps = Omit<SidebarNavProps, 'collapsed'> & {
  onClose: () => void;
};

const ROW = cn(
  'flex min-h-12 w-full items-center gap-3 px-4 py-2 text-start',
  'text-body no-underline hover:no-underline',
  'transition-colors duration-(--shell-motion-fast)',
  'active:bg-surface-2 outline-none focus-visible:bg-surface-2',
);

export function MobileMenu({
  permissions,
  role,
  counts,
  groups,
  onNavigate,
  onClose,
}: MobileMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const sections = useVisibleNav(permissions, role);
  const hasSettings = useVisibleSettings(permissions).length > 0;
  const { isLeafActive, isGroupActive, inSettings } = useActiveNav();
  const { isOpen, toggle } = groups;

  const badgeFor = (leaf: NavLeaf) => (leaf.badge ? counts[leaf.badge] : 0);

  const linkRow = (
    key: string,
    to: string,
    icon: NavIcon,
    text: string,
    active: boolean,
    badge = 0,
    badgeTone: 'primary' | 'accent' = 'primary',
    nested = false,
    locked = false,
  ) => (
    <Link
      key={key}
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(ROW, nested && 'ps-14')}
    >
      <IconTile icon={icon} active={active} small={nested} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          active ? 'font-semibold text-brand-text' : 'text-foreground',
        )}
      >
        {text}
      </span>
      {locked ? (
        <NavLock className="ms-0" />
      ) : (
        <NavCount value={badge} tone={badgeTone} />
      )}
      <ChevronRight
        aria-hidden
        className="size-4 shrink-0 text-secondary-400 rtl:rotate-180"
      />
    </Link>
  );

  const leafRow = (leaf: NavLeaf, nested = false) =>
    linkRow(
      leaf.id,
      leaf.href,
      leaf.icon,
      label(leaf.labelKey, leaf.labelFallback),
      isLeafActive(leaf),
      badgeFor(leaf),
      leaf.badge === 'pending' ? 'accent' : 'primary',
      nested,
      leaf.locked,
    );

  const groupRows = (group: NavGroup) => {
    const open = isOpen(group.id);
    const active = isGroupActive(group);
    const panelId = `menu-group-${group.id}`;
    return (
      <div key={group.id}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => toggle(group.id)}
          className={ROW}
        >
          <IconTile icon={group.icon} active={active} />
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              active ? 'font-semibold text-brand-text' : 'text-foreground',
            )}
          >
            {label(group.labelKey, group.labelFallback)}
          </span>
          {!open && (
            <NavCount
              value={group.badge ? counts[group.badge] : 0}
              tone="accent"
            />
          )}
          {group.locked && <NavLock className="ms-0" />}
          <ChevronDown
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-secondary-400 transition-transform duration-(--shell-motion-fast)',
              open && 'rotate-180',
            )}
          />
        </button>
        {open && (
          <div id={panelId} className="divide-y divide-border-subtle">
            {group.children
              .filter(
                (c) => !(c.kind === 'link' && c.hideWhenZero && !badgeFor(c)),
              )
              .map((child, i) =>
                child.kind === 'subheading' ? (
                  <p
                    key={`h-${i}`}
                    className="ps-14 pe-4 pb-1 pt-3 text-micro font-semibold uppercase tracking-wider text-secondary-400"
                  >
                    {label(child.labelKey, child.labelFallback)}
                  </p>
                ) : (
                  leafRow(child, true)
                ),
              )}
          </div>
        )}
      </div>
    );
  };

  return (
    // The drawer is portalled out of the shell root, so the shell's sizes and
    // timings have to be published again here for the classes below to read.
    <div
      className="flex h-full min-h-0 flex-col bg-surface-0"
      style={shellCssVars}
    >
      <div
        className="shrink-0 border-b border-border-default bg-surface-1"
        // Full screen on a phone: the bar runs up under the notch.
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-(--shell-header-h) items-center gap-2 px-4">
          <h2 className="min-w-0 flex-1 truncate text-h3 font-semibold text-foreground">
            {t('nav.more')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('shell.closeMenu')}
            className="flex size-control shrink-0 items-center justify-center rounded-full bg-surface-2 text-secondary-600 outline-none active:bg-surface-3 focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <X aria-hidden className="size-icon" />
          </button>
        </div>
      </div>

      <nav
        aria-label={t('nav.primary', 'Primary')}
        className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 pt-4"
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
        }}
      >
        <Card>
          <ClinicSwitcher asSheet />
          <BranchSwitcher />
        </Card>

        {/* Inside settings the main menu still shows: settings has its own
            overview page, and the menu is how you get back to the clinic. */}
        {sections.map((section) => (
          <section key={section.id} className="space-y-1.5">
            <h3 className="px-4 text-micro font-semibold uppercase tracking-wider text-secondary-400">
              {label(section.labelKey, section.labelFallback)}
            </h3>
            <Card divided>
              {section.items.map((item) =>
                item.kind === 'group' ? groupRows(item) : leafRow(item),
              )}
            </Card>
          </section>
        ))}

        <Card divided>
          {hasSettings &&
            linkRow(
              'settings',
              '/dashboard/settings',
              Settings,
              t('nav.settings'),
              inSettings,
            )}
          {linkRow(
            'feedback',
            '/dashboard/feedback',
            LifeBuoy,
            t('nav.helpAndFeedback'),
            isLeafActive({
              kind: 'link',
              id: 'feedback',
              labelKey: '',
              icon: LifeBuoy,
              href: '/dashboard/feedback',
            }),
          )}
        </Card>

        <a
          href="https://dentacares.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 pt-1 no-underline hover:no-underline"
        >
          <img
            src="/logo.svg"
            alt=""
            aria-hidden
            className="size-3.5 shrink-0 opacity-50"
          />
          <span className="text-micro leading-none text-secondary-400">
            {t('shell.poweredBy')}{' '}
            <span className="font-semibold text-secondary-500">
              {t('screens.sidebar.dentacares')}
            </span>
          </span>
        </a>
      </nav>
    </div>
  );
}

/** One inset group: a rounded card on the grey page, rows ruled between. */
function Card({
  divided = false,
  children,
}: {
  divided?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border-default bg-surface-1',
        // Clipped only when it holds rows: the clinic card's branch select
        // draws a focus ring that has to spill out of it.
        divided && 'divide-y divide-border-subtle overflow-hidden',
      )}
    >
      {children}
    </div>
  );
}

/** The tinted square behind a row's icon, as phone menus draw them. */
function IconTile({
  icon: Icon,
  active,
  small = false,
}: {
  icon: NavIcon;
  active: boolean;
  small?: boolean;
}) {
  if (small) {
    return (
      <Icon
        aria-hidden
        className={cn(
          'size-4 shrink-0',
          active ? 'text-brand-text' : 'text-secondary-500',
        )}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-md',
        active ? 'bg-brand text-brand-on' : 'bg-brand-soft text-brand-text',
      )}
    >
      <Icon className="size-4" />
    </span>
  );
}
